import React, { createContext, useContext, useState, ReactNode } from 'react';

export interface MenuItem {
  id: number;
  menuCode: string;
  menuName: string;
  menuPath: string | null;
  menuIcon: string | null;
  parentId: number | null;
  displayOrder: number;
  menuLevel: number;
  description: string | null;
  requiredRole: 'SUPER_ADMIN' | 'CLIENT';
  isActive: boolean;
  isExternal: boolean;
  createdAt: string;
  updatedAt: string | null;
  children: MenuItem[];
}

interface MenuContextType {
  menuItems: MenuItem[];
  setMenuItems: (items: MenuItem[]) => void;
  findMenuByPath: (path: string) => MenuItem | null;
}

const MenuContext = createContext<MenuContextType | undefined>(undefined);

export const MenuProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [menuItems, setMenuItems] = useState<MenuItem[]>([]);

  // 경로로 메뉴 찾기 (재귀적으로 모든 하위 메뉴 검색)
  const findMenuByPath = (path: string): MenuItem | null => {
    const searchInItems = (items: MenuItem[]): MenuItem | null => {
      for (const item of items) {
        // 정확한 경로 매칭
        if (item.menuPath === path) {
          return item;
        }
        // 자식 메뉴 탐색
        if (item.children && item.children.length > 0) {
          const found = searchInItems(item.children);
          if (found) return found;
        }
      }
      return null;
    };

    return searchInItems(menuItems);
  };

  const value: MenuContextType = {
    menuItems,
    setMenuItems,
    findMenuByPath,
  };

  return <MenuContext.Provider value={value}>{children}</MenuContext.Provider>;
};

export const useMenu = (): MenuContextType => {
  const context = useContext(MenuContext);
  if (context === undefined) {
    throw new Error('useMenu must be used within a MenuProvider');
  }
  return context;
};
